import { ProfileConfirm } from 'views/profile-confirm';
import {
	useCallback,
	useEffect,
	useLayoutEffect,
	useMemo,
	useState,
} from 'react';
import { Navigate, Route, Routes, useNavigate } from 'react-router-dom';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';

import { Loader } from '@storybook';
import { API_URL, ROLE } from 'constant';
import {
	AllowedInvitesState,
	BannerStatusState,
	BusinessPaymentCardState,
	BusinessRegistrationState,
	IBusinessRegistrationState,
	IsBannerStatusState,
	IsUserPermissionLoading,
	UserRolesPermissions,
	WebTokenState,
	loginState,
	subscriptionServiceState,
	userDataState,
	userState,
} from 'global-stores';
import {
	INIT_CONFIGURATION,
	initDB,
	useCookie,
	useCustomLoader,
	useFreePlan,
	useNetwork,
	useNotification,
} from 'hooks';
import { SignIn, SignUp } from 'views/authentication';
import { ComplexInitPipelines } from 'views/complex-onboarding-flow';
import { UploadDocuments } from 'views/upload-documents';
import { RoutesChildren, useUserRoles } from '../views/routes-children';
import { ROUTES } from './constants';
import { BusinessProcessing } from 'views/business-processing';
import { BusinessRejected } from 'views/business-rejected';
import { ApprovalStatus } from 'views/user-identity-flow';
import { CompletedPage } from 'views/completed';
import { EMAIL_VERIFICATION_MESSAGE, LinkExpired } from 'views/link-expired';
import { SignUpSessionStatusState } from 'views/new-user-popup';
import { useCompliance } from './hooks';
import { AcountUserTypes } from 'views/settings';
import { SuccessScreen } from 'views/success-screen';

initDB(INIT_CONFIGURATION);

const {
	LOGIN,
	SIGNUP,
	NO_ROUTE,
	PROFILE_CONFIRM,
	UPLODAD_DOCUMENTS,
	BUSINESS_PROCESSING,
	COMPLETED,
	BUSINESS_REJECTED,
} = ROUTES;
const { OWNER } = ROLE;

export const AllRoutes = () => {
	const [login, setLogin] = useRecoilState(loginState);
	const setUserD = useSetRecoilState(userDataState);
	const { get: getAccessToken } = useCookie();
	const setUserId = useSetRecoilState(userState);
	const setUserIdData = useSetRecoilState(userState);
	const setBannerStatus = useSetRecoilState(BannerStatusState);
	const setOnlyBannerStatus = useSetRecoilState(IsBannerStatusState);
	const [isEmailVerificationSuccess, setIsEmailVerificationSuccess] =
		useState(false);
	const [isEmailVerificationFailed, setIsEmailVerificationFailed] =
		useState(false);
	const webToken = useRecoilValue(WebTokenState);
	const { set: setCookie, get: getCookie } = useCookie();
	const localUserData = getCookie('user');
	const { post: getComplexPipelineInits } = useNetwork();
	const setPermissionLoading = useSetRecoilState(IsUserPermissionLoading);
	const [isUserLogin, setUserLogin] = useState(false);
	const [allowRoutes, setAllowRoutes] = useState(false);
	const [loading, setLoading] = useState(false);
	const [complexInitPipelines, setComplexInitPipelines] =
		useRecoilState(ComplexInitPipelines);
	const setBusinessRegistration = useSetRecoilState(BusinessRegistrationState);
	const setSubscription = useSetRecoilState(subscriptionServiceState);
	const setBusinessCard = useSetRecoilState(BusinessPaymentCardState);
	const setAllowedInvites = useSetRecoilState(AllowedInvitesState);
	const setUserPermissions = useSetRecoilState(UserRolesPermissions);
	const signUpSessionStatus = useRecoilValue(SignUpSessionStatusState);
	const navigate = useNavigate();
	const { fetchLoader } = useCustomLoader();

	const { loggedIn, accessToken: token } = useMemo(() => login ?? {}, [login]);
	const accessToken = useMemo(
		() =>
			webToken?.length > 0 ? webToken : token || localUserData?.accessToken,
		[localUserData?.accessToken, token, webToken]
	);
	const {
		haveKybKyc = false,
		haveKyc = false,
		accountType = '',
	} = useFreePlan();
	const { customUserTab } = useUserRoles();
	const { getComplianceData, fetchSessionDeatil } = useCompliance();
	const {
		get: userDetails,
		data: userData,
		error: userError,
		status: userStatus,
		post: userLogin,
	} = useNetwork();
	const { errorNotification } = useNotification();

	useLayoutEffect(() => {
		const redirectState = localStorage.getItem('redirectOrigin');
		if (accessToken && redirectState) {
			window.location.href = redirectState;
			localStorage.removeItem('redirectOrigin');
			return;
		}
	}, [accessToken]);

	const initUserData = useCallback(async () => {
		if (userData?.data?.length && userStatus) {
			setUserLogin(true);
			const {
				firstName,
				lastName,
				business,
				email,
				id,
				role,
				developer,
				isVerifiedEmail,
				isVerifiedPhone,
				invitedDate,
				isAgentPermitted,
				countryCode,
				phone,
				image,
				passKeyCount,
				sandboxStatus,
				userHash,
				createdAt,
				billingType,
				businessPrices,
				mfaEnabled,
				role: permissionRole,
			} = userData?.data?.[0] ?? {};

			const { onboardingService, simpliciSignService } = businessPrices ?? {};
			const { freeCredits: onboarding } = onboardingService ?? {};
			const { freeCredits: simpliciSign } = simpliciSignService ?? {};

			setAllowedInvites({
				onboarding: onboarding ?? 0,
				simpliciSign: simpliciSign ?? 0,
			});

			const userNameData = {
				...userData?.data[0],
				name: firstName + ' ' + lastName,
			};
			const {
				plan = '',
				status = '',
				_id: businessId,
				approveStatus,
				linkedInUrl,
				name: companyName,
				websiteUrl,
				subscription,
				accountType = '',
				kyb,
				kyc,
				billingAddress,
				ownBilling,
				differentCompany,
				parentBusiness,
				parentBusinessId = '',
				primaryUser,
				createdAt: businessCreatedAt,
			} = business;
			setSubscription(subscription ?? {});
			if (billingAddress) {
				setBusinessCard((prev: any) => ({
					...prev,
					billingAddress,
				}));
			}
			setLogin({
				...login,
				accessToken,
				business: businessId,
				email,
				name: firstName + ' ' + lastName,
				userId: id,
				plan,
				status,
				isVerifiedEmail,
				isVerifiedPhone,
				invitedDate,
				firstName,
				lastName,
				isAgentPermitted,
				countryCode,
				phone,
				image,
				developer,
				approveStatus,
				passKeyCount,
				linkedInUrl,
				companyName,
				websiteUrl,
				accountType,
				userHash,
				createdAt,
				kyb,
				kyc,
				role: role?.name ?? OWNER,
				roleId: role?._id ?? '',
				sandboxStatus: false,
				billingType,
				businessId: role?.businessId ?? '',
				ownBilling,
				differentCompany,
				mfaEnabled,
				parentBusiness,
				parentBusinessId,
				primaryUser,
				businessCreatedAt,
			});
			setUserPermissions(permissionRole);
			/* NEVER WRITE AN EMPTY TOKEN OVER A WORKING SESSION.
			 *
			 * This rewrote the `user` cookie unconditionally, and
			 * `accessToken ?? ''` is empty whenever the memo above has not
			 * resolved one at this moment. The cookie then reads
			 * {accessToken: ''}, getUser() computes loggedIn=false on the next
			 * read, every subsequent request 401s -- and the panel renders a
			 * BLANK page rather than the login screen, which is what made this
			 * so hard to recognise. A blank body with a stack of 401s in the
			 * console looks like a crash or a slow load, not like a session the
			 * app threw away itself.
			 *
			 * Keep the existing cookie when there is nothing better to write.
			 */
			setCookie('userEmail', { email });
			if (accessToken) {
				setCookie('user', {
					accessToken,
					userHash: userHash ?? '',
				});
			}
			setUserIdData({ userId: id });
			setUserD(userNameData);
			setUserId({
				userId: id,
			});

			setBannerStatus(true);
			// @TODO: AWADHESH
			// setBannerStatus(isVerifiedEmail === true || invitedDate != null);
			setOnlyBannerStatus(
				sandboxStatus ?? true
					? true
					: isVerifiedEmail === true || invitedDate != null
			);
			// Token set
			if (accessToken) await getComplianceData(accessToken);

			// Setting business Data of user
			setBusinessRegistration((prev: IBusinessRegistrationState) => ({
				...prev,
				approveStatus,
				accountType,
				kyc: prev?.kyc ? prev?.kyc : kyc,
				kyb: prev?.kyb ? prev?.kyb : kyb,
			}));
			setPermissionLoading(false);
			setUserLogin(false);
			setAllowRoutes(true);
		}
		if (userError) {
			errorNotification('Failed to user data.');
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [userData]);

	useEffect(() => {
		initUserData();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [userData]);

	//Intercom initilize for chat support
	useEffect(() => {
		if (window.location.host === 'app.simplici.io' && login.userHash) {
			try {
				if (login.email) {
					(window as any).Intercom('boot', {
						app_id: 'tphbl5gj',
						name: login.name,
						email: login.email,
						user_id: login.userId,
						user_hash: login.userHash,
					});

					(window as any).FS?.identify?.(login.userId);
					(window as any).FS?.setUserVars?.({
						displayName: login.name,
						email: login.email,
					});
				} else
					(window as any).Intercom('boot', {
						app_id: 'tphbl5gj',
						user_hash: login.userHash,
					});
				// eslint-disable-next-line no-empty
			} catch (error) { }
		}
	}, [login]);

	useEffect(() => {
		if (login?.isVerifiedEmail) return;
		const params = new URLSearchParams(window.location.search);
		const verificationType = params.get('type');
		const emailVerificationId = params.get('code');

		if (verificationType === 'emailVerification') {
			const payload = { code: emailVerificationId, type: 'verifyEmail' };
			const emailVerify = async () => {
				setLoading(true);
				await userLogin(API_URL.USER_LOGIN, payload).then((res: any) => {
					if (res?.success) {
						setIsEmailVerificationSuccess(true);
					} else setIsEmailVerificationFailed(true);
				});
				setLoading(false);
			};
			emailVerify();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const getComplexPipelineInit = useCallback(async () => {
		const resp = await getComplexPipelineInits(API_URL.COMPLEX_INIT, {});
		if (resp) {
			const updatedResp = JSON.parse(JSON.stringify(resp));
			//avinashSatschel remove ssn from stepVariable as we have merged ssn with addressVerification so we dont need to keep ssn object
			updatedResp.stepVariables = updatedResp.stepVariables.filter(
				(el: any) => el.key !== 'ssn'
			);
			setComplexInitPipelines(updatedResp);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		const cookies = getAccessToken('user');
		//@avinash : added accesstoken variable with or condition for webcomponent
		if (loggedIn && (cookies?.accessToken || accessToken)) {
			fetchLoader(cookies?.accessToken || accessToken);
			userDetails(`${API_URL.CLIENT_SIGNUP}`);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [loggedIn]);

	// Getting complex nodes
	useEffect(() => {
		if (!accessToken) return;
		if (Object.keys(complexInitPipelines).length === 0) {
			getComplexPipelineInit();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [accessToken, complexInitPipelines]);

	// Awadhesh: stop the splash loader for dashboard screen
	useEffect(() => {
		const element = document.querySelector('#splash-screen-loader');
		element?.remove();
	}, []);

	const handleClose = useCallback(() => {
		setIsEmailVerificationFailed(false);
		navigate(ROUTES.LOGIN);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		if (!signUpSessionStatus?.sessionIds.length) return;

		const { sessionIds } = signUpSessionStatus;
		if (accountType === AcountUserTypes.Person && !haveKyc) {
			sessionIds.forEach(session => {
				fetchSessionDeatil(session.sessionId);
			});
			return;
		}

		if (accountType === AcountUserTypes.Entity && !haveKybKyc) {
			sessionIds.forEach(session => {
				fetchSessionDeatil(session.sessionId);
			});
		}

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [signUpSessionStatus, haveKyc, accountType, haveKybKyc]);

	const renderMainRoutes = useMemo(() => {
		/* THE APPROVAL LANDING PAGE OUTRANKS THE AUTH BRANCH.
		   It is reached from a link in an email, which lands in whatever browser
		   the reader happens to be in — usually one holding no session, sometimes
		   one already signed in as somebody else. Both must render the page. Left
		   inside the signed-out block it would redirect a logged-in user to the
		   dashboard and silently swallow the approval; left inside the signed-in
		   block it would bounce everyone else to /login. So it is checked first,
		   before either. */
		if (window.location.pathname === PROFILE_CONFIRM) {
			return (
				<Routes>
					<Route path={PROFILE_CONFIRM} element={<ProfileConfirm />} />
				</Routes>
			);
		}
		if (accessToken || loggedIn) {
			if (loggedIn) {
				/* THE APPROVAL LOCKS BELOW WAIT FOR FRESH DATA — that is what `allowRoutes`
				   gates.

				   On a hard page load `login.approveStatus` is restored with the rest of the
				   session, so it can be STALE: an account the super admin approved while
				   this tab was away still reads UNDER_REVIEW until `initUserData` refreshes
				   it. Locking on the restored value would bounce a just-approved user to
				   /business-processing and then, once the fresh value lands and the lock
				   lifts, carry them to /dashboard via the `*` fallback — losing whatever
				   route they asked for.

				   `allowRoutes` is set true at the end of `initUserData`, after its awaited
				   fetches, so it means "the inputs these locks read are current",
				   including on the paths where a fetch failed. Until then the ordinary
				   tree renders, exactly as on the first paint; once it flips, an account
				   that is still waiting lands on its interstitial. */
				if (
					allowRoutes &&
					(signUpSessionStatus?.approveStatus?.toLowerCase() ===
					ApprovalStatus.Pending?.toLowerCase() ||
					login['approveStatus']?.toLowerCase() ===
					ApprovalStatus.Pending?.toLowerCase())
				) {
					return (
						<Routes>
							{allowRoutes && (
								<Route path={COMPLETED} element={<CompletedPage />} />
							)}
							<Route path="*" element={<Navigate to={COMPLETED} />} />
						</Routes>
					);
				}

				/* EVERY UNDER_REVIEW ACCOUNT WAITS ON "Submitted for review" — as production
				   (satschel/admin.panel) does, individual or entity alike (SIM-55, SIM-53).

				   v2 used to lift this lock for an ENTITY whose KYC and KYB had both passed
				   (`__simplici_skip_review_when_verified__`), so a verified entity got the
				   whole app while the super admin had not approved it yet. That was the
				   report behind both tickets: a sub-account's invited user "joined and
				   activated" with a full dashboard while the list said UNDER REVIEW, and a
				   business "approved and fully usable" while Super Admin said Under Review.
				   Neither had been approved; only the entity path could skip the wait, since
				   an individual has no KYB — which is why an individual under review always
				   landed here and an entity did not.

				   UNDER_REVIEW means the super admin has not decided yet. Until they do, the
				   account sees where it stands — the timeline on this screen — and cannot
				   send invites, run onboarding sessions or bill usage from the full app. */
				if (
					allowRoutes &&
					(signUpSessionStatus?.approveStatus?.toLowerCase() ===
					ApprovalStatus.UnderReview?.toLowerCase() ||
					login['approveStatus']?.toLowerCase() ===
					ApprovalStatus.UnderReview?.toLowerCase())
				) {
					return (
						<Routes>
							<Route path={UPLODAD_DOCUMENTS} element={<UploadDocuments />} />
							<Route
								path={BUSINESS_PROCESSING}
								element={<BusinessProcessing />}
							/>
							<Route path="*" element={<Navigate to={BUSINESS_PROCESSING} />} />
						</Routes>
					);
				}

				if (
					allowRoutes &&
					(signUpSessionStatus?.approveStatus?.toLowerCase() ===
					ApprovalStatus.Rejected?.toLowerCase() ||
					login['approveStatus'] === ApprovalStatus.Rejected?.toLowerCase())
				) {
					return (
						<Routes>
							<Route path={UPLODAD_DOCUMENTS} element={<UploadDocuments />} />
							<Route path={BUSINESS_REJECTED} element={<BusinessRejected />} />
							<Route path="*" element={<Navigate to={BUSINESS_REJECTED} />} />
						</Routes>
					);
				}
			}
			return <RoutesChildren />;
		}

		return (
			<Routes>
				<Route path={SIGNUP} element={<SignUp />} />
				<Route path={LOGIN} element={<SignIn />} />
				<Route path={UPLODAD_DOCUMENTS} element={<UploadDocuments />} />
				{/* __simplici_keep_verification_query__: carry the query string across
				    these redirects. The e-mail verification link is
				    `<panel>?type=emailVerification&code=…`, which lands on `/` — not a
				    route here — so it fell through to this catch-all, and a bare
				    `<Navigate to={LOGIN} />` rewrote the URL WITHOUT the query. The code
				    was gone before anything could read it, so the address was never
				    marked verified and sign-up sat on "Verify your email" for ever with
				    nothing on screen to say why. `/login` reads both params itself. */}
				<Route
					path={NO_ROUTE}
					element={<Navigate to={`${LOGIN}${window.location.search}`} replace />}
				/>
				<Route
					path="*"
					element={<Navigate to={`${LOGIN}${window.location.search}`} replace />}
				/>
			</Routes>
		);
	}, [
		accessToken,
		loggedIn,
		login,
		signUpSessionStatus?.approveStatus,
		allowRoutes,
	]);

	if (
		(customUserTab.length === 0 && (accessToken || loggedIn)) ||
		isUserLogin ||
		loading
	) {
		return (
			<div className="loader-center">
				<Loader />
			</div>
		);
	}

	if (isEmailVerificationFailed) {
		return (
			<LinkExpired
				onClose={handleClose}
				heading={EMAIL_VERIFICATION_MESSAGE.LABEL}
				description={EMAIL_VERIFICATION_MESSAGE.DESCRIPTION}
				actionLabel={EMAIL_VERIFICATION_MESSAGE.ACTION}
			/>
		);
	} else if (isEmailVerificationSuccess) {
		return <SuccessScreen />;
	}

	return renderMainRoutes;
};
